import React, { useEffect, useRef, useState } from 'react';

import type { BlobStyleProps, RippleColors } from './types';

const DEFAULT_RIPPLE_COLORS: RippleColors = {
  highlight: '#D8FFF0',
  mid: '#2CE39A',
  deep: '#064D33',
  ring: '#2CE39A',
};

/** Parse `#RRGGBB` into the `r,g,b` string used inside rgba() literals. */
function hexToRgbString(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '255,255,255';
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 0xff},${(n >> 8) & 0xff},${n & 0xff}`;
}

/**
 * Ripple blob — Siri-style expanding rings emitted at a rate scaled to
 * `level`. The core scales with level too. Ported from the wiki mock.
 */
export const RippleBlob: React.FC<BlobStyleProps & { colors?: RippleColors }> = ({ level, size, colors }) => {
  const [rings, setRings] = useState<{ id: number; strength: number; born: number }[]>([]);
  const idRef = useRef(0);
  const levelRef = useRef(level);
  useEffect(() => {
    levelRef.current = level;
  }, [level]);

  useEffect(() => {
    let alive = true;
    const loop = () => {
      if (!alive) return;
      const l = levelRef.current;
      if (l > 0.08) {
        setRings((rs) => [...rs, { id: idRef.current++, strength: l, born: performance.now() }]);
      }
      const delay = 180 + (1 - l) * 600;
      setTimeout(loop, delay);
    };
    loop();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => {
      const now = performance.now();
      setRings((rs) => rs.filter((r) => now - r.born < 2200));
    }, 400);
    return () => clearInterval(iv);
  }, []);

  const coreScale = 1 + level * 0.35;
  const c = colors ?? DEFAULT_RIPPLE_COLORS;
  const ringRgb = hexToRgbString(c.ring);
  const midRgb = hexToRgbString(c.mid);

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'grid',
        placeItems: 'center',
      }}
      aria-hidden
    >
      {rings.map((r) => (
        <span
          key={r.id}
          style={{
            position: 'absolute',
            width: size * 0.35,
            height: size * 0.35,
            borderRadius: '50%',
            border: `2.5px solid rgba(${ringRgb},${Math.min(0.95, 0.55 + 0.4 * r.strength)})`,
            boxShadow: `0 0 12px rgba(${ringRgb},${0.35 + 0.25 * r.strength})`,
            animation: 'voice-ripple-out 2.2s ease-out forwards',
          }}
        />
      ))}
      <div
        style={{
          width: size * 0.35,
          height: size * 0.35,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 35%, ${c.highlight}, ${c.mid} 55%, ${c.deep})`,
          boxShadow: `0 0 ${20 + level * 60}px rgba(${midRgb},${0.5 + level * 0.4})`,
          transform: `scale(${coreScale})`,
          transition: 'transform 80ms ease-out, box-shadow 120ms ease-out',
        }}
      />
      <style>{`
        @keyframes voice-ripple-out {
          0%   { transform: scale(0.9); opacity: 1; }
          100% { transform: scale(2.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default RippleBlob;
